import { ConnectomeNetwork } from '@/shared/model/connectome-network.model';

import { BinaryExpression } from '@/shared/model/binary-expression.model';
import { CmGroup } from '@/shared/model/cm-group.model';
import { CmGroupItem } from '@/shared/model/cm-group-item.model';
import { CmGroupsQueueMessage } from '@/shared/model/cm-group-queue-message.model';
import { CmGroupStatus } from '@/shared/model/cm-groupStatus.model';
import { ConnectomeNode } from '@/shared/model/connectome-node.model';
import { ContextualMemoryCollection } from '@/shared/model/contextual-memory-collection.model';
import axios from 'axios';
import { Module } from 'vuex';

const baseGroupsApiUrl = '/api/collections';
const getGroupURL = baseGroupsApiUrl + '/get';
const postCreateGroupURL = baseGroupsApiUrl + '/create';
const postUpdateGroupURL = baseGroupsApiUrl + '/update';
const postUpdateImageURLMapURL = baseGroupsApiUrl + '/updateRepesentative';
const postUpdateGroupNameURL = baseGroupsApiUrl + '/update/name';
const deleteGroupURL = baseGroupsApiUrl + '/delete';

export interface GroupsManagerStorable {
  indexSessionStorage: Map<string, string>;
  connectomeId: string;
  lang: string;
  groups: Array<CmGroupItem>;
  doHopSearch: boolean;
  groupsChanged: number;
  selectedScraps: any;
  updateScrapboardTitle: Array<any>;
}

export const groupsManagerStore: Module<GroupsManagerStorable, any> = {
  namespaced: true,
  state: {
    indexSessionStorage: new Map<string, string>(),
    connectomeId: null,
    lang: null,
    groups: new Array<CmGroupItem>(),
    doHopSearch: true,
    groupsChanged: 0,
    selectedScraps: {},
    updateScrapboardTitle: [],
  },
  getters: {
    getIndexSessionStorage: state => state.indexSessionStorage,
    getConnectomeId: state => state.connectomeId,
    getLang: State => State.lang,
    getGroups: state => state.groups,
    getSelectedScraps: state => state.selectedScraps,
    getGroupsFromDocument: state => (docId: string) => {
      if (!state.groups) {
        return [];
      }

      if (!docId) {
        return [];
      }

      if (!state.groups.filter(group => !group.isDraft && group.documentIdList.includes(docId))) {
        return [];
      }

      const groups = state.groups.filter(group => !group.isDraft && group.documentIdList.includes(docId)).map(x => x);

      if (!groups) {
        return [];
      }

      return groups;
    },
    getGroupsFromDocuments: (state, getters) => (docIds: Array<string>) => {
      if (!state.groups) {
        return [];
      }

      if (!docIds) {
        return [];
      }

      if (docIds.length == 0) {
        return [];
      }
      ////console.log(docIds);
      const result = new Array<CmGroupItem>();
      docIds.forEach(docId => {
        const listGroups = getters.getGroupsFromDocument(docId);
        listGroups.forEach(group => {
          if (!result.includes(group)) result.push(group);
        });
      });

      return result;
    },
    getGroupsFromName: (state, getters) => (name: string) => {
      if (!state.groups) {
        return [];
      }

      if (!name) {
        return [];
      }

      if (name.length == 0) {
        return [];
      }
      const regOption = new RegExp(name, 'ig');

      const groups = state.groups.filter((group: CmGroupItem) => !group.isDraft && group.name.match(regOption)).map(x => x);
      //console.log('*****[SVO]*****/(^^)/ getGroupsFromName', groups);
      if (!groups) {
        return [];
      }

      return groups;
    },
    isDoHopSearch: state => state.doHopSearch,
    //#region Interface flow December 5th SLT
    isGroupsChanged: state => state.groupsChanged,
    //#region
    getUpdateScrapboardTitle: state => state.updateScrapboardTitle,
  },
  mutations: {
    setSelectedScraps(state, selectedScraps) {
      state.selectedScraps = selectedScraps;
    },

    updateElementInGroups(state, { collectionId, isPinned }) {
      const groups = [...state.groups];
      groups.forEach(e => {
        if (e.collectionId == collectionId) {
          e.isPinned = isPinned;
        }
      });
      state.groups = groups;
      //update Pinned selectScrapboard
      const selectedScrap = { ...state.selectedScraps };
      if (selectedScrap[collectionId]) {
        selectedScrap[collectionId].isPinned = isPinned;
        state.selectedScraps = selectedScrap;
      }
    },
    setConnectomeId(state, connectomeId: string) {
      if (!connectomeId) {
        return;
      }

      state.connectomeId = connectomeId;
    },
    setLang(state, lang: string) {
      let language = 'en';
      if (lang) {
        switch (lang.toLowerCase()) {
          case 'en':
          case 'english':
          case 'uk':
          case 'us':
            language = 'en';
            break;
          case 'kr':
          case 'ko':
          case 'korea':
          case 'korean':
          case 'south korea':
          case '한국어':
            language = 'ko';
            break;
          default:
            language = 'en';
        }
      }
      state.lang = language;
    },
    setDoHopSearch(state, doHopSearch: boolean) {
      state.doHopSearch = doHopSearch;
    },
    setGroups(state, payload: { groups: Array<CmGroupItem> }) {
      if (!payload.groups) {
        return;
      }
      state.groups = payload.groups.map(x => x);
      state.groupsChanged++;
    },
    addToGroups(state, payload: { groups: Array<CmGroupItem> }) {
      if (!payload.groups) {
        return;
      }
      payload.groups.forEach(groupToAdd => {
        state.groups.push(groupToAdd);
      });
      state.groupsChanged++;
    },
    removeFromGroups(state, payload: { groups: Array<CmGroupItem> }) {
      if (!payload.groups) {
        return;
      }
      payload.groups.forEach(groupToRemove => {
        const indexGroupToRemove = state.groups.findIndex(group => group.groupId === groupToRemove.groupId);
        if (indexGroupToRemove > -1) {
          state.groups.splice(indexGroupToRemove, 1);
        }
      });
      state.groupsChanged++;
    },
    updateToGroups(state, payload: { groups: Array<CmGroupItem> }) {
      if (!payload.groups) {
        return;
      }
      //console.log('*****[SVO]*****/(^^)/ updateToGroups on grup-manager', payload);
      payload.groups.forEach(groupToReplace => {
        const indexGroupToRemove = state.groups.findIndex(group => group.groupId === groupToReplace.groupId);
        if (indexGroupToRemove > -1) {
          state.groups.splice(indexGroupToRemove, 1, groupToReplace);
          //console.log('*****[SVO]*****/(^^)/ updateToGroups index group to replace on grup-manager', indexGroupToRemove,groupToReplace);
        }
      });
      state.groupsChanged++;
    },
    resetData(state) {},
    setUpdateScrapboardTitle(state, updateScrapboardTitle) {
      state.updateScrapboardTitle = updateScrapboardTitle;
    },
  },
  actions: {
    setSelectedScraps: async (context, payload: { newVal }) => {
      return { status: 'OK' };
    },
    updateElementInGroups: async (context, { collectionId, isPinned }) => {
      context.commit('updateElementInGroups', { collectionId, isPinned });
      return { status: 'OK' };
    },
    // loadGroup: async (context, payload: { groupId: string }) => {
    //   if (!context.getters.getConnectomeId || !context.getters.getLang) {
    //     return;
    //   }
    //
    //   axios.defaults.headers.common['connectomeId'] = context.getters.getConnectomeId;
    //   axios.defaults.headers.common['lang'] = context.getters.getLang;
    //   axios.defaults.headers.common['collectionId'] = payload.groupId;
    //
    //   if (!payload.groupId) {
    //     return;
    //   }
    //
    //   const apiCallConnectomeData = new Promise<any>((resolve, reject) => {
    //     axios
    //       .get(getGroupURL, { params: { collectionId: payload.groupId } })
    //       .then(res => resolve(res))
    //       .catch(err => reject(err));
    //   });
    //
    //   return apiCallConnectomeData
    //     .then(res => {
    //       if (!res.data.body) {
    //         return;
    //       }
    //
    //       const response = res.data.body;
    //       //context.commit('setCurrentCollection', { collection: response });
    //
    //       return response;
    //     })
    //     .catch(reason => {
    //       //console.log('Reason', reason);
    //     });
    // },
    // logout(context) {},
    // loadUserContext: async (
    //   context,
    //   payload: {
    //     connectomeId: string;
    //     language: string;
    //   }
    // ) => {
    //   if (!payload) {
    //     return;
    //   }
    //
    //   context.commit('setConnectomeId', payload.connectomeId);
    //   context.commit('setLang', payload.language);
    //
    //   return context
    //     .dispatch('loadUserGroups')
    //     .then(res => {
    //       if (!res) {
    //         return { status: 'NOK', message: 'error load User Context', result: null };
    //       }
    //       return { status: 'OK', message: 'user context loaded', result: res };
    //     })
    //     .catch(reason => {
    //       //console.log('Reason', reason);
    //       return { status: 'NOK', message: 'error load User Context', result: null };
    //     });
    // },
    // loadUserGroups: async context => {
    //   if (!context.getters.getConnectomeId || !context.getters.getLang) {
    //     context.commit('setGroups', { groups: new Array<CmGroupItem>() });
    //     return;
    //   }
    //
    //   axios.defaults.headers.common['connectomeId'] = context.getters.getConnectomeId;
    //   //axios.defaults.headers.common['lang'] = context.getters.getLang;
    //
    //   const apiCallConnectomeData = new Promise<any>((resolve, reject) => {
    //     axios
    //       .get(getGroupsURL)
    //       .then(res => resolve(res))
    //       .catch(err => reject(err));
    //   });
    //
    //   return apiCallConnectomeData
    //     .then(res => {
    //       if (!res.data.body) {
    //         return;
    //       }
    //       const response = res.data.body;
    //       // //console.log('loadUserGroups', response);
    //       //console.log('*****[SVO]*****/(^^)/ loadUserGroups', response);
    //       const responseGroups = response.map(x => new CmGroupItem(x));
    //       context.commit('setGroups', { groups: responseGroups });
    //
    //       return response;
    //     })
    //     .catch(reason => {
    //       //console.log('Reason', reason);
    //     });
    // },
    createGroupOnBackend: async (
      context,
      payload: {
        group: CmGroupItem;
      }
    ) => {
      if (!context.getters.getConnectomeId || !context.getters.getLang) {
        return;
      }
      delete axios.defaults.headers.common['collectionId'];
      axios.defaults.headers.common['connectomeId'] = context.getters.getConnectomeId;
      axios.defaults.headers.common['lang'] = context.getters.getLang;
      axios.defaults.headers.common['doHopSearch'] = context.getters.isDoHopSearch;
      const newGroup = new ContextualMemoryCollection(null);
      newGroup.connectomeId = context.getters.getConnectomeId;
      newGroup.lang = context.getters.getLang;
      newGroup.documentIdList = payload.group.documentIdList.map(x => x);
      newGroup.isDraft = payload.group.isDraft;
      newGroup.name = payload.group.name ? payload.group.name : payload.group.name;
      //console.log('*****[SVO]*****/(^^)/ createGroupOnBackend', payload, newGroup);
      const apiCallConnectomeData = new Promise<any>((resolve, reject) => {
        axios
          .post(postCreateGroupURL, newGroup)
          .then(res => resolve(res))
          .catch(err => reject(err));
      });

      return apiCallConnectomeData
        .then(res => {
          //console.log('createGroupOnBackend payload', payload);

          if (!res.data.body) {
            return null;
          }
          const response = res.data.body;
          //console.log('create new group', response);
          return new CmGroup(response);
        })
        .catch(reason => {
          //console.log('Reason', reason);
          //console.log('create group', payload);
          //console.log('body', newGroup);
          return null;
        });
    },
    updateGroupOnBackend: async (
      context,
      payload: {
        group: CmGroupItem;
      }
    ) => {
      if (!context.getters.getConnectomeId || !context.getters.getLang) {
        return;
      }

      axios.defaults.headers.common['connectomeId'] = context.getters.getConnectomeId;
      axios.defaults.headers.common['lang'] = context.getters.getLang;
      axios.defaults.headers.common['doHopSearch'] = context.getters.isDoHopSearch;

      if (!payload.group) return;
      payload.group.modifiedDate = null;
      axios.defaults.headers.common['collectionId'] = payload.group.groupId;
      if (payload.group?.name) {
        axios.defaults.headers.common['name'] = payload.group.name;
      }
      if (!payload.group.collectionId) {
        payload.group.collectionId = payload.group.groupId;
      }

      const requestPayload = { ...payload.group };
      if (payload.group.imageURLMap) {
        // @ts-ignore
        requestPayload.imageURLMap = Object.fromEntries(payload.group.imageURLMap);
      }
      const apiCallConnectomeData = new Promise<any>((resolve, reject) => {
        axios
          .post(postUpdateGroupURL, requestPayload)
          .then(res => resolve(res))
          .catch(err => reject(err));
      });
      //console.log('updateGroupOnBackend SVO SVO SVO !!!', payload);
      return apiCallConnectomeData
        .then(res => {
          delete axios.defaults.headers.common['collectionId'];
          if (!res) {
            return null;
          }
          if (!res.data.body) {
            return null;
          }
          const response = res.data.body;

          return new CmGroup(response);
        })
        .catch(reason => {
          //console.log('Reason', reason);
          //console.log('update group', payload);
        })
        .finally(() => {
          delete axios.defaults.headers.common['collectionId'];
          delete axios.defaults.headers.common['name'];
        });
    },
    updateImageURLMapInScrapboardOnBackend: async (
      context,
      payload: {
        scrapboard: CmGroupItem;
      }
    ) => {
      if (!context.getters.getConnectomeId || !context.getters.getLang) {
        return;
      }

      axios.defaults.headers.common['connectomeId'] = context.getters.getConnectomeId;
      axios.defaults.headers.common['lang'] = context.getters.getLang;

      if (!payload.scrapboard) return;
      if (!payload.scrapboard.collectionId) {
        payload.scrapboard.collectionId = payload.scrapboard.groupId;
      }

      // console.log(payload.scrapboard.imageURLMap);
      // @ts-ignore
      // console.log(Object.fromEntries(payload.scrapboard.imageURLMap));
      const requestPayload = {
        collectionId: payload.scrapboard.collectionId,
        // @ts-ignore
        imageURLMap: payload.scrapboard.imageURLMap ? Object.fromEntries(payload.scrapboard.imageURLMap) : {},
        isPinned: payload.scrapboard.isPinned,
        documentIdList: payload.scrapboard.documentIdList,
        name: payload.scrapboard.name,
      };

      const apiCallConnectomeData = new Promise<any>((resolve, reject) => {
        axios
          .post(postUpdateImageURLMapURL, requestPayload)
          .then(res => resolve(res))
          .catch(err => reject(err));
      });
      return apiCallConnectomeData
        .then(res => {
          delete axios.defaults.headers.common['collectionId'];
          if (!res) {
            return null;
          }
          if (!res.data.body) {
            return null;
          }
          const response = res.data.body;

          return new CmGroup(response);
        })
        .catch(reason => {
          //console.log('Reason', reason);
          //console.log('update group', payload);
        })
        .finally(() => {
          delete axios.defaults.headers.common['collectionId'];
          delete axios.defaults.headers.common['name'];
        });
    },
    updateGroupNameOnBackend: async (
      context,
      payload: {
        group: CmGroupItem;
      }
    ) => {
      if (!context.getters.getConnectomeId || !context.getters.getLang) {
        return;
      }

      axios.defaults.headers.common['connectomeId'] = context.getters.getConnectomeId;
      axios.defaults.headers.common['lang'] = context.getters.getLang;
      axios.defaults.headers.common['doHopSearch'] = context.getters.isDoHopSearch;

      if (!payload.group) return;
      payload.group.modifiedDate = null;
      //axios.defaults.headers.common['collectionId'] = payload.group.groupId;

      //axios.defaults.headers.common['name'] = payload.group.name;

      if (!payload.group.collectionId) {
        payload.group.collectionId = payload.group.groupId;
      }

      //console.log('updateGroupNameOnBackend', payload.group);
      const apiCallConnectomeData = new Promise<any>((resolve, reject) => {
        axios
          .post(postUpdateGroupNameURL, payload.group)
          .then(res => resolve(res))
          .catch(err => reject(err));
      });

      return apiCallConnectomeData
        .then(res => {
          //delete axios.defaults.headers.common['collectionId'];
          //delete axios.defaults.headers.common['name'];
          if (!res) {
            return null;
          }
          if (!res.data.body) {
            return null;
          }
          const response = res.data.body;
          //console.log('update group', response);

          return new CmGroup(response);
        })
        .catch(reason => {
          //console.log('Reason', reason);
          //console.log('update group', payload);
        })
        .finally(() => {
          delete axios.defaults.headers.common['collectionId'];
          delete axios.defaults.headers.common['name'];
        });
    },
    getGroupByIdFromBackend: async (
      context,
      payload: {
        groupId: string;
      }
    ) => {
      axios.defaults.headers.common['connectomeId'] = context.getters.getConnectomeId;
      axios.defaults.headers.common['lang'] = context.getters.getLang;
      axios.defaults.headers.common['collectionId'] = payload.groupId;

      if (!payload.groupId) {
        return;
      }

      const apiCallConnectomeData = new Promise<any>((resolve, reject) => {
        axios
          .get(getGroupURL, { params: { collectionId: payload.groupId } })
          .then(res => resolve(res))
          .catch(err => reject(err));
      });

      return apiCallConnectomeData
        .then(res => {
          if (!res.data.body) {
            return;
          }

          const response = res.data.body;

          return new CmGroup(response);
        })
        .catch(reason => {
          //console.log('Reason', reason);
        });
    },
    deleteGroupOnBackend: async (
      context,
      payload: {
        groupId: string;
      }
    ) => {
      if (!context.getters.getConnectomeId || !context.getters.getLang) {
        return;
      }

      if (!payload.groupId) {
        return;
      }
      axios.defaults.headers.common['connectomeId'] = context.getters.getConnectomeId;
      axios.defaults.headers.common['lang'] = context.getters.getLang;
      axios.defaults.headers.common['collectionId'] = payload.groupId;

      const apiCallConnectomeData = new Promise<any>((resolve, reject) => {
        axios
          .delete(deleteGroupURL)
          .then(res => resolve(res))
          .catch(err => reject(err));
      });

      return apiCallConnectomeData
        .then(res => {
          delete axios.defaults.headers.common['collectionId'];
          //console.log('deleteGroupOnBackend', res);
          if (!res) {
            return;
          }

          if (!res.data) {
            return;
          }
          const response = res.data.body;
          //console.log('deleteGroupOnBackend response', response);

          return response;
        })
        .catch(reason => {
          //console.log('Reason', reason);
        })
        .finally(() => {});
    },
    //#region Interface flow December 5th SLT
    //
    // NEW INTERFACE
    //
    getAllGroups: async context => {
      //return list of Group contains this docIds
      //console.log('*****[SVO]*****/(^^)/ getAllGroups', context.getters.getGroups.filter(group => !group.isDraft).map(x => x));
      return {
        status: 'OK',
        message: 'user context loaded',
        result: context.getters.getGroups.filter(group => !group.isDraft).map(x => x),
      };
    },
    getGroupsByDocIds: (
      context,
      payload: {
        docIds: Array<string>;
      }
    ) => {
      if (!payload.docIds) {
        return { status: 'NOK', message: 'docIds list is undefined', result: new Array<string>() };
      }

      if (payload.docIds.length == 0) {
        return { status: 'OK', message: 'docIds list is empty', result: new Array<string>() };
      }

      return {
        status: 'OK',
        message: 'list group containing the docIds',
        result: context.getters.getGroupsFromDocuments(payload.docIds),
      };
    },
    getGroupsByName: (
      context,
      payload: {
        name: string;
      }
    ) => {
      if (!payload.name) {
        return { status: 'NOK', message: 'name is undefined', result: new Array<string>() };
      }

      if (payload.name.length == 0) {
        return { status: 'OK', message: 'name is empty', result: new Array<string>() };
      }

      return {
        status: 'OK',
        message: 'list group containing the docIds',
        result: context.getters.getGroupsFromName(payload.name),
      };
    },
    getGroupsByGroupIds: (
      context,
      payload: {
        groupIds: Array<string>;
      }
    ) => {
      if (!payload.groupIds) {
        return { status: 'NOK', message: 'groupIds list is undefined', result: new Array<string>() };
      }

      if (payload.groupIds.length == 0) {
        return { status: 'OK', message: 'groupIds list is empty', result: new Array<string>() };
      }

      return {
        status: 'OK',
        message: 'list group ',
        result: context.getters.getGroups.filter(group => !group.isDraft && payload.groupIds.includes(group.groupId)).map(x => x),
      };
    },
    createGroupWithDocIds: async (context, payload: { name: string; documentIdList: Array<string>; addToWorkspace: boolean }) => {
      if (!payload?.documentIdList) {
        return { status: 'NOK', message: 'The input groupId list is empty', result: null };
      }

      const groupExport = new CmGroupItem(null);
      groupExport.isDraft = false;
      groupExport.documentIdList = payload.documentIdList.map(x => x);
      if (payload.name) {
        groupExport.name = payload.name;
      }
      //console.log('*****[SVO]*****/(^^)/ createGroupWithDocIds', payload, groupExport);
      //console.log('*******[TEST SVO] createGroupWithDocIds', payload);
      return context
        .dispatch('createGroupOnBackend', {
          group: groupExport,
        })
        .then((res: CmGroup) => {
          //console.log('[*******[TEST SVO] createGroupWithDocIds result from createGroupOnBackend', res);

          if (!res) {
            return { status: 'NOK', message: 'Group is not created, something wrong', result: null };
          }

          if (!res.groupId) {
            return { status: 'NOK', message: 'groupId is not defined, something wrong', result: null };
          }

          let allDocidsIncluded = true;
          groupExport.documentIdList.forEach(docIdAdded => {
            if (res.documentIdList.includes(docIdAdded)) {
              allDocidsIncluded = false;
            }
          });
          const result = new CmGroupItem(res);
          context.commit('addToGroups', { groups: [result] });

          if (!allDocidsIncluded) {
            return { status: 'OK', message: "group didn't create properly, something went wrong", result: result };
          }
          return { status: 'OK', message: 'group created', result: result };
        });
    },
    updateDocumentsInGroup: async (context, payload: { groupId: string; documentIdList: Array<string> }) => {
      if (!payload?.groupId) {
        return { status: 'NOK', message: 'The input groupId is empty', result: null };
      }

      if (!payload?.documentIdList) {
        return { status: 'NOK', message: 'The input groupId list is empty', result: null };
      }
      const groups = context.getters.getGroups
        .filter(group => !group.isDraft && payload.groupId.localeCompare(group.groupId) == 0)
        .map(x => x);

      if (!groups || groups.length == 0) {
        return { status: 'NOK', message: 'The input groupId not found', result: null };
      }

      const workspaceExport = new CmGroupItem(groups[0]);
      workspaceExport.collectionId = payload.groupId;
      workspaceExport.groupId = payload.groupId;
      workspaceExport.isDraft = false;
      workspaceExport.documentIdList = payload.documentIdList;

      //console.log('*****[SVO]*****/(^^)/ updateDocumentsInGroup', workspaceExport);
      //console.log('updateDocumentsInGroup SVO SVO SVO !!!', payload);
      return context
        .dispatch('updateGroupOnBackend', {
          group: workspaceExport,
        })
        .then((res: CmGroup) => {
          //console.log('updateDocumentsInGroup result from updateGroupOnBackend', res);

          if (!res) {
            return { status: 'NOK', message: 'Workspace is not updated, something wrong', result: null };
          }

          if (!res.groupId) {
            return { status: 'NOK', message: 'groupId is not defined, something wrong', result: null };
          }
          const result = new CmGroupItem(res);
          let allDocidsIncluded = true;
          workspaceExport.documentIdList.forEach(docIdAdded => {
            if (res.documentIdList.includes(docIdAdded)) {
              allDocidsIncluded = false;
            }
          });
          context.commit('updateToGroups', { groups: [result] });

          if (!allDocidsIncluded) {
            return { status: 'OK', message: "workspace didn't updated properly, something went wrong", result: result };
          }
          return { status: 'OK', message: 'group updated', result: result };
        });
    },
    updateImageURLMapInGroup: async (context, payload: { group: CmGroupItem }) => {
      if (!payload?.group?.groupId) {
        return { status: 'NOK', message: 'The input groupId is empty', result: null };
      }

      return context
        .dispatch('updateImageURLMapInScrapboardOnBackend', {
          scrapboard: payload.group,
        })
        .then((res: CmGroup) => {
          //console.log('updateDocumentsInGroup result from updateGroupOnBackend', res);

          if (!res) {
            return { status: 'NOK', message: 'Workspace is not updated, something wrong', result: null };
          }

          if (!res.groupId) {
            return { status: 'NOK', message: 'groupId is not defined, something wrong', result: null };
          }
          const result = new CmGroupItem(res);

          context.commit('updateToGroups', { groups: [result] });

          return { status: 'OK', message: 'group updated', result: result };
        });
    },
    deleteGroup: async (context, payload: { groupId: string }) => {
      if (!payload?.groupId) {
        return { status: 'NOK', message: 'The input groupId is empty', result: null };
      }

      return context
        .dispatch('deleteGroupOnBackend', {
          groupId: payload.groupId,
        })
        .then(res => {
          //console.log('deleteGroup result from deleteGroupOnBackend', res);
          if (!res) {
            return { status: 'NOK', message: 'group is not deleted properly', result: null };
          }
          const response = new CmGroupStatus(JSON.parse(res));

          if (response.code == 1030) {
            const groupToremove = new CmGroupItem(null);
            groupToremove.groupId = payload.groupId;
            context.commit('removeFromGroups', { groups: [groupToremove] });

            // if (context.getters.getCurrentWorkspace.collectionIdIncluded?.includes(payload.collectionId)) {
            //   context
            //     .dispatch('removeGroupsFromWorkspace', {
            //       collectionIdList: [payload.collectionId],
            //     })
            //     .then(res => //console.log('deleteGroup removeGroupsFromWorkspace', res));
            // }
            // if (!context.getters.getCurrentWorkspace?.collectionId) {
            //   return { status: 'OK', message: 'collection deleted', result: null };
            // }
            // if (context.getters.getCurrentWorkspace.collectionId.localeCompare(payload.collectionId) == 0) {
            //   context
            //     .dispatch('resetWorkspace', {
            //       force: true,
            //     })
            //     .then(res => //console.log('handleWebsocketMessage removeGroupsFromWorkspace', res));
            // }
            return { status: 'OK', message: 'group deleted', result: response };
          } else {
            return { status: 'NOK', message: 'group is not deleted properly', result: response };
          }
        });
    },
    moveGroupsToGroup: async (context, payload: { groupIdsSource: Array<string>; groupIdTarget: string }) => {
      const groupTargetList: Array<CmGroupItem> = context.getters.getGroups
        .filter(group => !group.isDraft && payload.groupIdTarget == group.groupId)
        .map(x => x);

      if (groupTargetList.length == 0) {
        return { status: 'OK', message: 'group target doesn t exist', result: null };
      }

      const groupList: Array<CmGroupItem> = context.getters.getGroups
        .filter(group => !group.isDraft && (payload.groupIdsSource.includes(group.groupId) || payload.groupIdTarget == group.groupId))
        .map(x => x);

      if (groupList.length == 0) {
        return { status: 'OK', message: 'No group to move', result: groupTargetList[0] };
      }

      const docList = new Set<string>();
      groupList.forEach((group, index, groups) => {
        if (group?.documentIdList) {
          group.documentIdList.forEach((docid, indexDoc, documents) => {
            docList.add(docid);
          });
        }
      });
      if (docList.size == 0) {
        return { status: 'OK', message: 'No doc to move', result: groupTargetList[0] };
      }
      //console.log('moveGroupsToGroup SVO SVO SVO !!!', payload);
      return context.dispatch('updateDocumentsInGroup', { groupId: payload.groupIdTarget, documentIdList: Array.from(docList) });
    },
    renameGroup: async (context, payload: { newName: string; groupId: string }) => {
      if (!payload?.groupId) {
        return { status: 'NOK', message: 'The input groupId is empty', result: null };
      }

      if (!payload?.newName) {
        return { status: 'NOK', message: 'Name is empty is empty', result: null };
      }

      const workspaceExport = new CmGroupItem(null);
      workspaceExport.groupId = payload.groupId;
      workspaceExport.isDraft = false;
      workspaceExport.name = payload.newName;

      return context
        .dispatch('updateGroupNameOnBackend', {
          group: workspaceExport,
        })
        .then((res: CmGroup) => {
          return {
            status: 'OK',
            message: 'group renamed',
            result: context.getters.getGroups.filter(group => (workspaceExport.groupId = group.groupId)).map(x => x),
          };
        });
    },
    //#endregion
    handleWebsocketMessage: async (context, message: CmGroupsQueueMessage) => {
      if (!message) {
        return { status: 'NOK', message: 'Message undefined', result: null };
      }
      if (!message.connectomeId) {
        return { status: 'NOK', message: 'Message connectomeId undefined', result: message };
      }

      if (message.connectomeId.localeCompare(context.getters.getConnectomeId) != 0) {
        return { status: 'NOK', message: 'Message for other connectome', result: message };
      }

      if (!message.collectionId) {
        return { status: 'NOK', message: 'Message collectionId undefined', result: message };
      }

      if (!message.status) {
        return { status: 'NOK', message: 'Message status undefined', result: message };
      }
      console.log('*****[SVO]*****/(^^)/ handleWebsocketMessage on group-manager', message);

      if (message.status.code == 1030) {
        const GroupToremove = new CmGroupItem(null);
        GroupToremove.groupId = message.collectionId;
        GroupToremove.collectionId = message.collectionId;
        context.commit('removeFromGroups', { groups: [GroupToremove] });
        return { status: 'OK', message: 'Group deleted', result: message };
      }
      if (
        message.status.code == 1050 ||
        message.status.code == 1020 ||
        message.status.code == 1010 ||
        message.status.code == 1001 ||
        message.status.code == 1000
      ) {
        return context
          .dispatch('getGroupByIdFromBackend', {
            groupId: message.groupId,
          })
          .then((res: CmGroup) => {
            //console.log('handleWebsocketMessage result from getGroupByIdFromBackend', res);
            if (!res) {
              return { status: 'NOK', message: 'Group is empty', result: message };
            }
            if (res.isDraft) {
              return { status: 'OK', message: 'Group it is workspace', result: res };
            }
            const group = new CmGroupItem(res);
            if (message.status.code == 1050 || message.status.code == 1020 || message.status.code == 1001 || message.status.code == 1000) {
              context.commit('updateToGroups', { groups: [group] });
              // if (context.getters.getCurrentWorkspace.collectionIdIncluded?.includes(message.collectionId)) {
              //   context.dispatch('internUpdateWorkspace').then(res => {
              //     if (!res) {
              //       return;
              //     }
              //     //console.log('update Workspace', res);
              //     if (res.status === 'OK') {
              //       context.commit('setCurrentConnectome', { nodeList: res.result.nodeList });
              //     }
              //   });
              // }

              // if (context.getters.getCurrentWorkspace.collectionId?.localeCompare(message.collectionId) == 0 && res.nodeList) {
              //   context.commit('setCurrentConnectome', { nodeList: res.nodeList });
              // }
              return { status: 'OK', message: 'group is updated', result: group };
            }

            if (message.status.code == 1010) {
              context.commit('addToGroups', { groups: [group] });
              return { status: 'OK', message: 'group added', result: message };
            }
            context.dispatch('loadUserGroups');
            return { status: 'NOK', message: 'no action', result: message };
          });
      } else {
        return { status: 'NOK', message: 'Message status undefined', result: message };
      }
    },
    setlang(context, payload: { newVal: any }) {
      context.commit('setLang', payload.newVal);
    },
  },
};

function buildBinaryCascadeFromArray(type: string, listValues: Array<string>) {
  if (!listValues) {
    return null;
  }

  if (listValues.length < 1) {
    return null;
  }

  if (listValues.length == 1) {
    return listValues[0];
  }

  const exp = new BinaryExpression();
  exp.type = type;
  exp.left = listValues[0];
  listValues.splice(0, 1);
  exp.right = buildBinaryCascadeFromArray(type, listValues);

  return exp;
}

function convertLocalStorageToMiniConnectomeNetworkData(connectomeId: string, vertices: any, edges: any) {
  if (!connectomeId) {
    return null;
  }

  if (!vertices) {
    return null;
  }

  if (!edges) {
    return null;
  }

  const res = new ConnectomeNetwork(connectomeId, null, vertices, edges);
  return res;
}

function convertLocalStorageToArrayString(obj: any) {
  if (!obj) {
    return new Array<string>();
  }

  const map = JSON.parse(obj);
  const list = new Array<string>();
  for (let i = 0; i < map.length; i++) {
    list.push(map[i]);
  }
  if (!map) {
    return new Array<string>();
  }

  return list;
}

function convertLocalStorageToMapStringArrayString(obj: any) {
  if (!obj) {
    return new Map<string, Array<string>>();
  }

  const map = new Map<string, Array<string>>(JSON.parse(obj));
  if (!map) {
    return new Map<string, Array<string>>();
  }
  return map;
}

function convertLocalStorageToArrayNodes(obj: any) {
  if (!obj) {
    return new Array<ConnectomeNode>();
  }

  const list = new Array<ConnectomeNode>(JSON.parse(obj));
  if (!list) {
    return new Array<ConnectomeNode>();
  }
  return list;
}
